import { NgModule } from '@angular/core';
import { Routes, RouterModule } from '@angular/router';
import { AuthGuard } from '@guards/auth/auth.guard';
import { PermissionGuard } from '@guards/guards/permission.guard';
import { ExcludeHivGuard } from '@guards/guards/exclude-hiv.guard';
import { AuthLayoutComponent } from '@layout/auth-layout/auth-layout.component';
import { AdminLayoutComponent } from '@layout/admin-layout/admin-layout.component';
import { AboutComponent } from './pages/about/about.component';
import { PageNotFoundComponent } from '@components/page-not-found/page-not-found.component';
import { NavigationModel } from '@models/navigation.model';
import { HIVAssistantPermission } from './_constants/permissions';
const navigationModel: NavigationModel = new NavigationModel();
const routes: Routes = [
  {
    path: 'auth',
    pathMatch: 'full',
    redirectTo: 'auth/login',
  },
  {
    path: 'auth',
    component: AuthLayoutComponent,
    children: [
      {
        path: '',
        loadChildren: () =>
          import('@layout/auth-layout/auth-layout.module').then(
            (m) => m.AuthLayoutModule
          ),
      },
    ],
  },
  {
    path: '',
    component: AdminLayoutComponent,
    canActivate: [AuthGuard],
    children: [
      {
        path: '',
        loadChildren: () =>
          import('@pages/home/home.module').then((m) => m.HomeModule),
        canActivate: [PermissionGuard],
        data: {
          permissions: navigationModel.routes.find((r) => r.path === 'home')
            ?.permission,
          isAssistant: Boolean(
            navigationModel.routes.find((r) => r.path === 'home')?.isAssistant
          ),
        },
      },
      {
        path: 'patientList',
        loadChildren: () =>
          import('@pages/patientList/patientList.module').then(
            (m) => m.PatientListModule
          ),
        canActivate: [PermissionGuard],
        data: {
          permissions: navigationModel.routes.find(
            (r) => r.path === 'patientList'
          )?.permission,
          isAssistant: Boolean(
            navigationModel.routes.find((r) => r.path === 'patientList')
              ?.isAssistant
          ),
        },
      },
      {
        path: 'treatment-lines',
        loadChildren: () =>
          import('@pages/treatment-lines/treatment-lines.module').then(
            (m) => m.TreatmentLinesModule
          ),
        canActivate: [ExcludeHivGuard],
      },
      {
        path: 'pep-patient',
        loadComponent: () =>
          import('@pages/PEP/home-pep-patient/home-pep-patient.component').then((m) => m.HomePepPatientComponent),
        canActivate: [PermissionGuard],
        data: {
          permissions: navigationModel.routes.find(
            (r) => r.path === 'pep-patient'
          )?.permission,
          isAssistant: Boolean(
            navigationModel.routes.find((r) => r.path === 'pep-patient')
              ?.isAssistant
          ),
        },
      },
      {
        path: 'pep-patients-list',
        loadComponent: () =>
          import('@pages/PEP/pep-patients-list/pep-patients-list.component').then((m) => m.PepPatientsListComponent),
      },
      {
        path: 'create-pep-patient',
        loadComponent: () =>
          import('@pages/PEP/pep-patient/pep-patient.component').then(
            (c) => c.PepPatientComponent
          ),
        canActivate: [PermissionGuard],
        data: {
          permissions: [HIVAssistantPermission],
          isAssistant: false,
        },
      },
      {
        path: 'appointments',
        children: [
          {
            path: 'fullcalendar',
            loadComponent: () =>
              import('./pages/appointments-fullCalendar/appointments-fullCalendar.component').then(
                (m) => m.AppointmentsFullCalendarComponent
              ),
            canActivate: [PermissionGuard],
            data: {
              permissions: navigationModel.routes.find(
                (r) => r.path === 'appointments/fullcalendar'
              )?.permission,
              isAssistant: Boolean(
                navigationModel.routes.find((r) => r.path === 'appointments/fullcalendar')
                  ?.isAssistant
              ),
            },
          },
          {
            path: '',
            redirectTo: 'fullcalendar',
            pathMatch: 'full'
          }
        ]
      },
      {
        path: 'carnivita-pms',
        loadChildren: () =>
          import('@pages/carnivita-pms/carnivita-pms.module').then(
            (m) => m.CarnivitaPmsModule
          ),
        canActivate: [PermissionGuard],
        data: {
          permissions: navigationModel.routes.find(
            (r) => r.path === 'carnivita-pms'
          )?.permission,
          isAssistant: Boolean(
            navigationModel.routes.find((r) => r.path === 'carnivita-pms')
              ?.isAssistant
          ),
        },
      },
      {
        path: 'esomeprazole-pms',
        loadChildren: () =>
          import('@pages/esomeprazole-pms/esomeprazole-pms.module').then(
            (m) => m.EsomeprazolePmsModule
          ),
        canActivate: [PermissionGuard],
        data: {
          permissions: navigationModel.routes.find(
            (r) => r.path === 'esomeprazole-pms'
          )?.permission,
          isAssistant: Boolean(
            navigationModel.routes.find((r) => r.path === 'esomeprazole-pms')
              ?.isAssistant
          ),
        },
      },
      {
        path: 'hiv',
        loadChildren: () =>
          import('@pages/hiv-clinic-model/hiv.module').then((m) => m.HIVModule),
        canActivate: [PermissionGuard],
        data: {
          permissions: [HIVAssistantPermission]
        }
      },
      {
        path: 'medication-orders',
        loadChildren: () =>
          import('@pages/medication-orders/medication-orders.module').then(
            (m) => m.MedicationOrdersModule
          ),
      },
      {
        path: 'stock-transfer',
        loadChildren: () =>
          import('@pages/stock-transfer/stock-transfer.module').then(
            (m) => m.StockTransferModule
          ),
        canActivate: [PermissionGuard],
        data: {
          permissions: navigationModel.routes.find(
            (r) => r.path === 'stock-transfer'
          )?.permission,
          isAssistant: Boolean(
            navigationModel.routes.find((r) => r.path === 'stock-transfer')
              ?.isAssistant
          ),
        },
      },
      {
        path: 'patient-transfer',
        loadChildren: () =>
          import('@pages/patient-transfer/patient-transfer.module').then(
            (m) => m.PatientTransferModule
          ),
        canActivate: [PermissionGuard],
        data: {
          permissions: navigationModel.routes.find(
            (r) => r.path === 'patient-transfer'
          )?.permission,
          isAssistant: Boolean(
            navigationModel.routes.find((r) => r.path === 'patient-transfer')
              ?.isAssistant
          ),
        },
      },
      {
        path: 'pending-prescriptions',
        loadComponent() {
          return import(
            '@pages/pending-prescriptions/pending-prescriptions.component'
          ).then((m) => m.PendingPrescriptionsComponent);
        },
        canActivate: [PermissionGuard],
        data: {
          permissions: ['HIVDoctor'],
        },
      },
      {
        path: 'medication-stocks',
        loadChildren: () =>
          import('@pages/medication-stock/medication-stock.module').then(
            (m) => m.MedicationStockModule
          ),
        canActivate: [PermissionGuard],
        data: {
          permissions: ['DoctorAssistant'],
          isAssistant: true,
        },
      },
      {
        path: 'drug-drug-interaction',
        loadChildren: () =>
          import(
            '@pages/drug-drug-interaction/drug-drug-interaction.module'
          ).then((m) => m.DrugDrugInteractionModule),
        canActivate: [PermissionGuard],
        data: {
          permissions: navigationModel.routes.find(
            (r) => r.path === 'drug-drug-interaction'
          )?.permission,
          isAssistant: Boolean(
            navigationModel.routes.find(
              (r) => r.path === 'drug-drug-interaction'
            )?.isAssistant
          ),
        },
      },
      {
        path: 'assistants-management',
        loadChildren: () =>
          import(
            '@pages/assistants-management/assistants-management.module'
          ).then((m) => m.AssistantsManagementModule),
        canActivate: [PermissionGuard],
        data: {
          permissions: navigationModel.routes.find(
            (r) => r.path === 'assistants-management'
          )?.permission,
          isAssistant: Boolean(
            navigationModel.routes.find(
              (r) => r.path === 'assistants-management'
            )?.isAssistant
          ),
        },
      },
      {
        path: 'patients-analysis',
        loadChildren: () =>
          import('@pages/patients-analysis/patients-analysis.module').then(
            (m) => m.PatientsAnalysisModule
          ),
        canActivate: [PermissionGuard],
        data: {
          permissions: navigationModel.routes.find(
            (r) => r.path === 'patients-analysis'
          )?.permission,
          isAssistant: Boolean(
            navigationModel.routes.find((r) => r.path === 'patients-analysis')
              ?.isAssistant
          ),
        },
      },
      {
        path: 'genuphil-woman-pms',
        loadChildren: () =>
          import('@pages/genuphil-woman-pms/genuphil-woman-pms.module').then(
            (m) => m.GenuphilWomanPmsModule
          ),
        canActivate: [PermissionGuard],
        data: {
          permissions: navigationModel.routes.find(
            (r) => r.path === 'genuphil-woman-pms'
          )?.permission,
          isAssistant: Boolean(
            navigationModel.routes.find((r) => r.path === 'genuphil-woman-pms')
              ?.isAssistant
          ),
        },
      },
      {
        path: 'conventin-xr-pms',
        loadChildren: () =>
          import('@pages/conventin-xr-pms/conventin-xr-pms.module').then(
            (m) => m.ConventinXrPmsModule
          ),
        canActivate: [PermissionGuard],
        data: {
          permissions: navigationModel.routes.find(
            (r) => r.path === 'conventin-xr-pms'
          )?.permission,
          isAssistant: Boolean(
            navigationModel.routes.find((r) => r.path === 'conventin-xr-pms')
              ?.isAssistant
          ),
        },
      },
      {
        path: 'clinic-finances',
        loadChildren: () =>
          import('@pages/clinic-finances/clinic-finances.module').then(
            (m) => m.ClinicFinancesModule
          ),
        canActivate: [ExcludeHivGuard],
      },
      {
        path: 'clinic-analysis',
        loadChildren: () =>
          import('@pages/clinic-analysis/clinic-analysis.module').then(
            (m) => m.ClinicAnalysisModule
          ),
        canActivate: [PermissionGuard],
        data: {
          permissions: navigationModel.routes.find(
            (r) => r.path === 'clinic-analysis'
          )?.permission,
          isAssistant: Boolean(
            navigationModel.routes.find((r) => r.path === 'clinic-analysis')
              ?.isAssistant
          ),
        },
      },
      {
        path: 'roche-psp',
        loadChildren: () =>
          import('@pages/roche-psp/roche-psp.module').then(
            (m) => m.RochePspModule
          ),
        canActivate: [PermissionGuard],
        data: {
          permissions: navigationModel.routes.find((r) => r.path === 'roche-psp')?.permission,
          isAssistant: Boolean(navigationModel.routes.find((r) => r.path === 'roche-psp')?.isAssistant),
        },
      },
      {
        path: 'create-rx/:patientId',
        loadChildren: () =>
          import('@pages/create-rx/create-rx.module').then(
            (m) => m.CreateRxModule
          ),
        canActivate: [PermissionGuard],
        data: {
          isAssistant: false,
        },
      },
      {
        path: 'create-rx/:patientId/:prescriptionId',
        loadChildren: () =>
          import('@pages/create-rx/create-rx.module').then(
            (m) => m.CreateRxModule
          ),
        canActivate: [PermissionGuard],
        data: {
          isAssistant: false,
        },
      },
      {
        path: 'about',
        component: AboutComponent,
      },
      {
        path: 'page-not-found',
        component: PageNotFoundComponent,
      },
    ],
  },
  {
    path: '**',
    redirectTo: 'page-not-found',
  },
];

@NgModule({
  imports: [
    RouterModule.forRoot(routes, {
      scrollPositionRestoration: 'enabled',
      anchorScrolling: 'enabled',
    }),
  ],
  exports: [RouterModule],
})
export class AppRoutingModule { }

export const appModuleRoutingComponents = [
  AuthLayoutComponent,
  AdminLayoutComponent,
  AboutComponent,
  PageNotFoundComponent,
];
